import { jsonInit } from '@shigo-ui/api-client';
import { z } from 'zod';
import { assignedAgencySchema } from './agencyAccess';

import { createShigoApiClient, type ShigoApiConfig } from '../../lib/shigo-api-client';
import { parseRuntimeNetworkBrandDetails } from '../../lib/networkBranding';
import type {
  ConfirmPasswordResetRequest,
  ForgotPasswordRequest,
  ShigoCredentials,
  ShigoSession,
  ShigoUser,
} from './model';
import { normalizeNetworkFeatures } from './networkFeatures';
import {
  normalizeShigoPermissions,
  resolveLegacyRolePermissions,
  ShigoPermissionSource,
} from './permissions';

const loginPayloadSchema = z.object({
  accessToken: z.string().trim().min(1),
});
const passwordRecoveryResponseSchema = z.unknown();

const loginResponseSchema = z
  .union([z.string(), loginPayloadSchema])
  .transform((value, context) => {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return loginPayloadSchema.parse(JSON.parse(value));
    } catch {
      context.addIssue({ code: 'custom', message: 'Invalid login response.' });
      return z.NEVER;
    }
  });

const shigoUserSchema = z
  .object({
    id: z.string().optional(),
    email: z.string().optional(),
    name: z.string().optional(),
    username: z.string().optional(),
    publisherIds: z.array(z.string()).default([]),
    agencies: z.array(assignedAgencySchema).optional(),
    roles: z.array(z.string()).default([]),
    rights: z.array(z.string()).optional(),
    useStitcher: z.boolean().nullish(),
    userRoles: z
      .array(
        z.object({
          id: z.number().optional(),
          name: z.string().optional(),
          permissions: z.array(z.string()).default([]),
        }),
      )
      .optional(),
    network: z
      .object({
        features: z.array(z.string()).nullish(),
        brandDetails: z.unknown().optional(),
      })
      .nullish(),
  })
  .loose();

export async function createShigoSession(
  credentials: ShigoCredentials,
  config: ShigoApiConfig,
  fetcher?: typeof fetch,
): Promise<ShigoSession> {
  const anonymousClient = createShigoApiClient(config, undefined, fetcher);
  const login = await anonymousClient.request({
    path: '/login',
    schema: loginResponseSchema,
    init: jsonInit('POST', credentials),
  });
  const authenticatedClient = createShigoApiClient(config, login.accessToken, fetcher);
  const user = await authenticatedClient.request({
    path: '/api/users/me',
    schema: shigoUserSchema,
  });

  return { accessToken: login.accessToken, user: normalizeShigoUser(user) };
}

export function requestPasswordReset(
  payload: ForgotPasswordRequest,
  config: ShigoApiConfig,
  fetcher?: typeof fetch,
) {
  return createShigoApiClient(config, undefined, fetcher).request({
    path: '/api/forgot-password',
    schema: passwordRecoveryResponseSchema,
    init: jsonInit('POST', payload),
  });
}

export function confirmPasswordReset(
  payload: ConfirmPasswordResetRequest,
  config: ShigoApiConfig,
  fetcher?: typeof fetch,
) {
  return createShigoApiClient(config, undefined, fetcher).request({
    path: '/api/forgot-password/confirm-password-reset',
    schema: passwordRecoveryResponseSchema,
    init: jsonInit('POST', payload),
  });
}

function normalizeShigoUser(user: z.infer<typeof shigoUserSchema>): ShigoUser {
  const rolePermissions = user.userRoles?.flatMap((role) => role.permissions) ?? [];
  // Legacy built-in roles are returned with rights: [], so an empty array is not an assignment.
  const hasBackendPermissionModel = Boolean(user.userRoles?.length || user.rights?.length);
  const permissions = normalizeShigoPermissions(
    hasBackendPermissionModel
      ? [...rolePermissions, ...(user.rights ?? [])]
      : resolveLegacyRolePermissions(user.roles),
  );
  const networkBrandDetails = parseRuntimeNetworkBrandDetails(user.network?.brandDetails);

  return {
    ...(user.id ? { id: user.id } : {}),
    ...(user.email ? { email: user.email } : {}),
    ...(user.name ? { name: user.name } : {}),
    ...(user.username ? { username: user.username } : {}),
    publisherIds: user.publisherIds,
    ...(user.agencies ? { agencies: user.agencies } : {}),
    roles: user.roles,
    permissions: [...new Set(permissions)],
    networkFeatures: normalizeNetworkFeatures(user.network?.features ?? []),
    useStitcher: user.useStitcher === true,
    ...(networkBrandDetails ? { networkBrandDetails } : {}),
    permissionSource: hasBackendPermissionModel
      ? ShigoPermissionSource.Backend
      : ShigoPermissionSource.LegacyRole,
  };
}
